/**
 * useFeedback hook — feedback logic (API calls, trigger guards, storage).
 * Adapted from frontend/src/hooks/useFeedback.ts for React Native.
 */
import { useCallback, useEffect } from 'react'
import { Platform } from 'react-native'
import { apiClient } from '../api/client'
import { events } from '../lib/posthog'
import { persistentStorage } from '../lib/storageAdapter'
import { appEvents } from '../lib/events'

const TRIGGER_PREFIX = 'feedback_triggered_'
const VIEW_COUNT_KEY = 'feedback_button_views'
const SESSION_COUNT_KEY = 'app_session_count'
const MAX_PULSE_VIEWS = 5
const MIN_SESSIONS_FOR_TRIGGERS = 3

let lastTriggerScheduledAt = 0
const TRIGGER_COOLDOWN_MS = 300_000 // 5 min between auto-triggers
let nativeTriggersPreloaded = false

export type FeedbackTrigger =
  | 'manual'
  | 'post_onboarding'
  | 'first_quote_view'
  | 'quote_accepted'
  | 'chat_engaged'
  | 'inbox_explored'
  | 'settings_visited'
  | 'dashboard_return'
  | 'error_encountered'
  | 'returning_user'
  | 'first_dashboard_visit'

const ALL_TRIGGERS: FeedbackTrigger[] = [
  'post_onboarding', 'first_quote_view', 'quote_accepted', 'chat_engaged',
  'inbox_explored', 'settings_visited', 'dashboard_return', 'error_encountered', 'returning_user',
  'first_dashboard_visit',
]

export function useFeedback() {
  // On native, pre-load trigger keys from async storage into cache (once)
  useEffect(() => {
    if (Platform.OS !== 'web' && !nativeTriggersPreloaded) {
      nativeTriggersPreloaded = true
      ALL_TRIGGERS.forEach(t => persistentStorage.load(`${TRIGGER_PREFIX}${t}`))
      persistentStorage.load(VIEW_COUNT_KEY)
      persistentStorage.load(SESSION_COUNT_KEY)
    }
  }, [])

  const submitFeedback = useCallback(
    async (rating: number, comment: string | null, screen: string, trigger: FeedbackTrigger): Promise<{ success: boolean; aiResponse?: string }> => {
      try {
        const res = await apiClient.feedback.submit({ rating, comment, page_url: screen, trigger })
        events.feedbackSubmitted(rating, trigger, screen)
        return { success: true, aiResponse: res.data?.ai_response }
      } catch {
        return { success: false }
      }
    },
    []
  )

  const hasFiredTrigger = useCallback((triggerId: FeedbackTrigger): boolean => {
    return persistentStorage.get(`${TRIGGER_PREFIX}${triggerId}`) === 'true'
  }, [])

  const markTriggerFired = useCallback((triggerId: FeedbackTrigger): void => {
    persistentStorage.set(`${TRIGGER_PREFIX}${triggerId}`, 'true')
  }, [])

  const canShowTrigger = useCallback((): boolean => {
    // Cooldown: at least 5 min since last scheduled trigger
    if (Date.now() - lastTriggerScheduledAt < TRIGGER_COOLDOWN_MS) return false
    return true
  }, [])

  const shouldFireTrigger = useCallback(
    (triggerId: FeedbackTrigger): boolean => {
      if (hasFiredTrigger(triggerId) || !canShowTrigger()) return false
      lastTriggerScheduledAt = Date.now()
      return true
    },
    [hasFiredTrigger, canShowTrigger]
  )

  const getAndIncrementViewCount = useCallback((): number => {
    const current = parseInt(persistentStorage.get(VIEW_COUNT_KEY) || '0', 10)
    persistentStorage.set(VIEW_COUNT_KEY, String(current + 1))
    return current
  }, [])

  const shouldPulse = useCallback((): boolean => {
    const count = parseInt(persistentStorage.get(VIEW_COUNT_KEY) || '0', 10)
    return count < MAX_PULSE_VIEWS
  }, [])

  const getSessionCount = useCallback((): number => {
    return parseInt(persistentStorage.get(SESSION_COUNT_KEY) || '0', 10)
  }, [])

  const openFeedback = useCallback(
    (trigger: FeedbackTrigger, question?: string) => {
      appEvents.emit('open-feedback', { trigger, question })
    },
    []
  )

  return {
    submitFeedback,
    hasFiredTrigger,
    markTriggerFired,
    canShowTrigger,
    shouldFireTrigger,
    getAndIncrementViewCount,
    getSessionCount,
    shouldPulse,
    openFeedback,
    MIN_SESSIONS_FOR_TRIGGERS,
  }
}
